import 'reflect-metadata';
import {AppTableDisplayColumn} from "projects/nter-ui-kit/src/lib/webkit/components/crud-table/crud-table.model";
import {
  displayPercentageWithoutDecimal,
  displayRandAmountWithDecimal
} from '@ui-kit/framework/util/static/number-format-util';
import {MatSort} from "@angular/material/sort";

/**
 * Decorators used to generate a jsonforms schema from a class. Example implementation:
 * export class Module {
 *   @Number('ID')
 *   @ReadOnly
 *   @DisplayOrder(1)
 *   id?: number;
 *
 *   @String('Code')
 *   @Required
 *   @DisplayOrder(3)
 *   code?: string;
 *
 *   @Required
 *   @String('Display name')
 *   @DisplayOrder(4)
 *   name?: string;
 *
 *   @Required
 *   @String('Description')
 *   @DisplayOrder(5)
 *   description?: string;
 *
 *   @Required
 *   @String('System Code')
 *   @DisplayOrder(2)
 *   @Options([{ CONSOLE: 'Landlord portal' }, { PORTAL: 'Tenant portal' }])
 *   platformSystemCode?: string;
 *
 *   @Hidden
 *   serialVersionUID?: number;
 *
 *   @Boolean('Active')
 *   @DisplayOrder(6)
 *   active?: boolean = true;
 *
 *   @Date('label', 'yyyy-MM-dd')
 *   @Hidden
 *   createDate?: string;
 *
 *   @Hidden
 *   @Number('')
 *   createUserId?: number;
 *
 *   @Hidden
 *   @Date('')
 *   updateDate?: string;
 *
 *   @Hidden
 *   @Number('')
 *   updateUserId?: number;
 *
 *   constructor(obj: any = {}) {
 *     Object.assign(this, obj);
 *   }
 * }
 * @param description
 * @constructor
 */


/* ===================  COMMON DECORATORS      ================== */
/**
 * Decorator used to generate a text input for a jsonforms schema from a class or a crud-table configuration for dynamically
 * generating table data.
 *
 * Table: Example implementation:
 * export class Module {
 *   //.. rest of code
 *
 *   @String('Code')
 *   @Required
 *   @DisplayOrder(3)
 *   code?: string;
 *   }
 *
 *   Will generate the following object that represents displayColumns:
 *   displayColumns: AppTableDisplayColumn[] = [
 *   AppTableDisplayColumn.INSTANCE({ colName: 'code', displayLabel: 'Code' }),
 *   ];
 *
 * Form (jsonform): Example implementation:
 * export class Module {
 *  //.. rest of code
 *  @String('Code')
 *  @Required
 *  @DisplayOrder(3)
 *  code?: string;
 *  }
 *
 * Will generate the following object that represents the jsonform schema:
 * const { jsonSchema, uiSchema, initialData } = generateJsonFormsSchemas(new Module());
 * with value:
 * {
 *   "jsonSchema": {
 *     "type": "object",
 *     "properties": {
 *       "code": {
 *         "type": "string",
 *         "description": "Code"
 *       }
 *     },
 *     "required": [
 *       "code"
 *     ]
 *   },
 *   "uiSchema": {
 *     "type": "VerticalLayout",
 *     "elements": [
 *       {
 *         "type": "Control",
 *         "scope": "#/properties/code",
 *         "label": "Code",
 *         "options": {
 *           "hidden": false,
 *           "readonly": false
 *         }
 *       },
 *     ]
 *   }
 * @param description
 */
export function String(description: string) {
  return function (target: any, propertyKey: string) {
    Reflect.defineMetadata('design:type', 'string', target, propertyKey);
    Reflect.defineMetadata(
      'jsonforms:description',
      description,
      target,
      propertyKey
    );
  };
}

/**
 * Decorator used to generate a number input for a jsonforms schema from a class or a crud-table configuration for dynamically
 * generating table data.
 * For full details see {@link String}.
 * @param description
 */
export function Number(description: string) {
  return function (target: any, propertyKey: string) {
    Reflect.defineMetadata('design:type', 'number', target, propertyKey);
    Reflect.defineMetadata(
      'jsonforms:description',
      description,
      target,
      propertyKey
    );
  };
}

/**
 * Decorator used to generate a percentage input for a jsonforms schema from a class or a crud-table configuration for dynamically
 * generating table data.
 * @param description
 */
export function Percentage(description: string) {
  return function (target: any, propertyKey: string) {
    // Call the Number decorator to handle number type metadata
    Number(description)(target, propertyKey);

    // Add currency-specific metadata
    Reflect.defineMetadata('jsonforms:description', description, target, propertyKey);
    Reflect.defineMetadata('table:displayFunction', displayPercentageWithoutDecimal, target, propertyKey);
  };
}

/**
 * Decorator used to generate a currency input for a jsonforms schema from a class or a crud-table configuration for dynamically
 * generating table data.
 * @param description
 * @param currencySymbol
 */
export function Currency(description: string, currencySymbol: string = '$') {
  return function (target: any, propertyKey: string) {
    // Call the Number decorator to handle number type metadata
    Number(description)(target, propertyKey);

    // Add currency-specific metadata
    Reflect.defineMetadata('jsonforms:currency', currencySymbol, target, propertyKey);
    Reflect.defineMetadata('jsonforms:description', description, target, propertyKey);
    Reflect.defineMetadata('table:displayFunction', displayRandAmountWithDecimal, target, propertyKey);
  };
}

/**
 * Decorator to attach a formatting method to a class property.
 * @param formatter - The function to format the value.
 */
export function DisplayFunction(formatter: (value: any) => any) {
  return function (target: any, propertyKey: string) {
    Reflect.defineMetadata('table:displayFunction', formatter, target, propertyKey);
  };
}


/**
 * Decorator to attach a formatting method to a class property on export to csv, excel or pdf.
 * @param formatter - The function to format the value.
 */
export function ExportFunction(formatter: (value: any) => any) {
  return function (target: any, propertyKey: string) {
    Reflect.defineMetadata('table:exportFunction', formatter, target, propertyKey);
  };
}

/**
 * Decorator to attach a method to be executed on cell click.
 *
 * The handler receives the cell's RAW value (`element[colName]`), not the
 * {@link DisplayFunction}-formatted display text — so pairing e.g.
 * `@DisplayFunction(displayUuid)` (shows a shortened uuid) with
 * `@ClickFunction(copyToClipboard)` copies the FULL uuid.
 *
 * @param process - The function to be executed on click, given the raw cell value.
 * @param tooltip - Optional hover tooltip on the cell, e.g. 'Click to copy'.
 */
export function ClickFunction(process: (value: any) => any, tooltip?: string) {
  return function (target: any, propertyKey: string) {
    Reflect.defineMetadata('table:clickFunction', process, target, propertyKey);
    if (tooltip) {
      Reflect.defineMetadata('table:clickTooltip', tooltip, target, propertyKey);
    }
  }
}

/**
 * Decorator to specify name of the item heading on export to csv, excel or pdf.
 * @param description - The name of the column heading.
 */
export function ExportLabel(description: string) {
  return function (target: any, propertyKey: string) {
    Reflect.defineMetadata('table:exportLabel', description, target, propertyKey);
  };
}

/**
 * Decorator used to generate a boolean input for a jsonforms schema from a class or a crud-table configuration for dynamically
 * generating table data.
 * For full details see {@link String}.
 * @param description
 */
export function Integer(description: string) {
  return function (target: any, propertyKey: string) {
    Reflect.defineMetadata('design:type', 'integer', target, propertyKey);
    Reflect.defineMetadata(
      'jsonforms:description',
      description,
      target,
      propertyKey
    );
  };
}

/**
 * Decorator used to generate a boolean input for a jsonforms schema from a class or a crud-table configuration for dynamically
 * generating table data.
 * For full details see {@link String}.
 * @param description
 */
export function Boolean(description: string) {
  return function (target: any, propertyKey: string) {
    Reflect.defineMetadata('design:type', 'boolean', target, propertyKey);
    Reflect.defineMetadata(
      'jsonforms:description',
      description,
      target,
      propertyKey
    );
  };
}

/**
 * Decorator used to generate a date input for a jsonforms schema from a class or a crud-table configuration for dynamically
 * generating table data.
 * For full details see {@link String}.
 * @param description
 * @param dateFormat
 */
export function Date(description: string, dateFormat?: string) {
  return function (target: any, propertyKey: string) {
    Reflect.defineMetadata('design:type', 'date', target, propertyKey);
    Reflect.defineMetadata(
      'jsonforms:description',
      description,
      target,
      propertyKey
    );
    Reflect.defineMetadata(
      'jsonforms:dateFormat',
      dateFormat,
      target,
      propertyKey
    );
  };
}

/**
 * Decorator used to generate a hidden input for a jsonforms schema from a class or a crud-table configuration for dynamically
 * generating table data. This will determine the order of the field in the table and form.
 * @param order
 */
export function DisplayOrder(order: number) {
  return function (target: any, propertyKey: string) {
    Reflect.defineMetadata('jsonforms:order', order, target, propertyKey);
  };
}

/* ===================  CRUD-ITEM DECORATORS   ================== */

/**
 * Decorator used to set the field value as the identifier for a form endpoint.
 * eg: url: http://localhost:8080/api/v1/endpoint/{id}
 * export class Module {
 *   @Number('ID')
 *   @ReadOnly
 *   @DisplayOrder(1)
 *   id?: number;
 *
 *   @String('Code')
 *   @Required
 *   @DisplayOrder(3)
 *   @FormEndpointIdentifier
 *   code?: string;
 *  }
 *
 * Will generate the following object that represents the form endpoint:
 * const module = new Module();
 * const apiEndpoint = 'http://localhost:8080/api/v1/endpoint/{code}'
 * formEndpoint = generateFormEndpoint(apiEndpoint, module);
 *
 * @param target
 * @param propertyKey
 * @constructor
 */
// export function FormEndpointIdentifier(target: any, propertyKey: string) {
//   Reflect.defineMetadata('form:endpoint-identifier', true, target, propertyKey);
// }


/**
 * Generate the following object that represents the form endpoint by looking for the identifier field using the
 * field name specified in the templated string url:
 * const module = new Module({code: 'SYSTEM_MODULE_CODE_A', id: 1});
 * const apiEndpoint = 'http://localhost:8080/api/v1/endpoint/{code}'
 * formEndpoint = generateFormEndpoint(apiEndpoint, module);
 * formEndpoint = 'http://localhost:8080/api/v1/endpoint/SYSTEM_MODULE_CODE_A'
 *
 * and
 * const module = new Module({code: 'SYSTEM_MODULE_CODE_A', id: 1});
 * const apiEndpoint = 'http://localhost:8080/api/v1/endpoint/{id}}/{code}'
 * formEndpoint = generateFormEndpoint(apiEndpoint, module);
 * formEndpoint = 'http://localhost:8080/api/v1/endpoint/1/SYSTEM_MODULE_CODE_A'
 *
 */
export function buildEndpointFromTemplate(templateString: string, model: any): string {
  return templateString.replace(/{(\w+)}/g, (match, key) => {
    return typeof model[key] !== 'undefined' ? model[key] : match;
  });
}


/**
 * Decorator used to generate a visible but readonly input for a jsonforms schema from a class.
 */
export function ReadOnly(target: any, propertyKey: string) {
  Reflect.defineMetadata('jsonforms:readonly', true, target, propertyKey);
}

/**
 * Decorator used to generate a hidden input for a jsonforms schema from a class.
 */
export function Required(target: any, propertyKey: string) {
  Reflect.defineMetadata('jsonforms:required', true, target, propertyKey);
}

/**
 * Decorator used to generate a hidden input for a jsonforms schema from a class.
 */
export function Hidden(target: any, propertyKey: string) {
  Reflect.defineMetadata('jsonforms:hidden', true, target, propertyKey);
}

/**
 * Decorator used to generate a jsonforms specific conditions for an input for a jsonforms schema from a class
 * @param conditions
 */
export function Conditions(conditions: any | any[]) {
  return function (target: any, propertyKey: string) {
    Reflect.defineMetadata(
      'jsonforms:conditions',
      conditions,
      target,
      propertyKey
    );
  };
}

/**
 * Decorator used to generate a jsonforms specific options for an input for a jsonforms schema from a class
 * @param options
 * @param multiple - if true, the input will be a multiple select
 */
export function Options(
  options: { [key: string]: string }[],
  multiple: boolean = false
) {
  return function (target: any, propertyKey: string) {
    Reflect.defineMetadata('jsonforms:options', options, target, propertyKey);
    Reflect.defineMetadata('jsonforms:multiple', multiple, target, propertyKey);
  };
}


/* ===================  CRUD-TABLE DECORATORS  ================== */

/**
 * Decorator used to generate a table ID column for a crud-table configuration for dynamically generating table data.
 * @param target
 * @param propertyKey
 * @constructor
 */
export function TableIdentifier(target: any, propertyKey: string) {
  Reflect.defineMetadata('table:identifier', true, target, propertyKey);
}

/**
 * Decorator used include a column in the list of columns that can be displayed in the table, but not specifically initially displayed.
 * @param target
 * @param propertyKey
 * @constructor
 */
export function TableDisplayed(target: any, propertyKey: string) {
  Reflect.defineMetadata('table:displayed', true, target, propertyKey);
}

/**
 * Decorator used to include a column in the initially displayed columns (replaces @TableDisplayed, don't need to use both)
 * @param target
 * @param propertyKey
 * @constructor
 */
export function TableDisplayedDefault(target: any, propertyKey: string) {
  // Reflect.defineMetadata('table:displayed', true, target, propertyKey);
  TableDisplayed(target, propertyKey);
  Reflect.defineMetadata('table:displayedDefault', true, target, propertyKey);
}

/**
 * Decorator used to generate a list of default search terms for a crud-table configuration for dynamically generating table data.
 * The search terms are in the format: [{column:value}]
 * e.g. :
 * @DefaultSearchTerms(['active:true'])
 * export class TestClass {
 *   private field: string;
 *  }
 *
 *  Will generate the following object that represents the default search terms:
 *  ['active:true']
 * @param searchTerms
 * @constructor
 */
export function DefaultSearchTerms(searchTerms: string[]) {
  return function (target: any) {
    Reflect.defineMetadata('table:defaultSearchTerms', searchTerms, target);
  };
}

/* ===============    Methods    ================== */
function getType(
  metadataType: any
): string | { type: string; format?: string } {
  if (metadataType === 'date') {
    return {type: 'string', format: 'date-time'};
  }

  // An explicit @String/@Integer/… type decorator stores a string here. Without
  // one, `design:type` falls back to the reflected constructor (String, Number,
  // Array, …) — a function — so read its name rather than calling `.toLowerCase`
  // on the function and crashing (e.g. an untyped array field on a form model).
  const typeName =
    typeof metadataType === 'function' ? metadataType.name : metadataType;

  return typeof typeName === 'string' ? typeName.toLowerCase() : 'string';
}

export function generateJsonFormsSchemas(classType: any) {
  const instance = new classType();
  const jsonSchemaProperties: any = {};
  const uiSchemaElements: any[] = [];
  const required: string[] = [];

  Object.getOwnPropertyNames(instance).forEach((key) => {
    if (key !== 'constructor') {
      // Common setup for all elements
      const type = Reflect.getMetadata('design:type', instance, key);
      const description =
        Reflect.getMetadata('jsonforms:description', instance, key) || key;
      const dateFormat = Reflect.getMetadata(
        'jsonforms:dateFormat',
        instance,
        key
      );
      const isRequired = Reflect.getMetadata('jsonforms:required', instance, key);
      const isHidden =
        Reflect.getMetadata('jsonforms:hidden', instance, key) || false; // Ensure default false
      const isReadOnly =
        Reflect.getMetadata('jsonforms:readonly', instance, key) || false; // Ensure default false
      const order = Reflect.getMetadata('jsonforms:order', instance, key) || 1000;
      const conditions = Reflect.getMetadata(
        'jsonforms:conditions',
        instance,
        key
      );
      const options = Reflect.getMetadata('jsonforms:options', instance, key);
      const multiple = Reflect.getMetadata('jsonforms:multiple', instance, key);

      const element: any = {
        type: 'Control',
        scope: `#/properties/${key}`,
        label: description,
        options: {
          hidden: isHidden,
          readonly: isReadOnly,
        },
        order: order,
      };

      if (type === 'date') {
        jsonSchemaProperties[key] = {
          type: 'string',
          format: dateFormat || 'date-time',
          description: description,
        };
      } else {
        jsonSchemaProperties[key] = {
          type: getType(type),
          description: description,
        };
      }

      if (isRequired) required.push(key);

      if (options) {
        const enumValues = options.map((option: any) => Object.keys(option)[0]);
        const enumTitles = options.map(
          (option: any) => option[Object.keys(option)[0]]
        );
        jsonSchemaProperties[key].oneOf = options.map((option: any) => {
          const [value, label] = Object.entries(option)[0];
          return {const: value, title: label};
        });

        element.options = {
          ...element.options,
          customselect: true,
        };

        if (multiple) {
          jsonSchemaProperties[key].type = 'array';
          jsonSchemaProperties[key].items = {type: 'string', enum: enumValues};
          jsonSchemaProperties[key].uniqueItems = true;
          element.options.multiple = true;
        } else {
          jsonSchemaProperties[key].enum = enumValues;
        }
      }

      if (conditions) {
        element.rule = {
          effect: conditions.effect,
          condition: {
            scope: conditions.scope,
            schema: conditions.schema,
          },
        };
      }

      uiSchemaElements.push(element);
    }
  });

  uiSchemaElements.sort((a, b) => a.order - b.order);

  const jsonSchema = {
    type: 'object',
    properties: jsonSchemaProperties,
    required: required,
  };

  const uiSchema = {
    type: 'VerticalLayout',
    elements: uiSchemaElements
      .filter(f => !f.options.hidden)
      .map((element) => {
        delete element.order;
        return element;
      }),
  };

  return {jsonSchema, uiSchema};
}

/**
 * Generates the table column names and ids based on the same CRUD decorators used for the form.
 * The following decorators are available:
 *
 *   ========================================
 *   Shared between crud-table and crud-item
 *   ========================================
 *
 *   //Used to  link the column code and display label for the table
 *   @Number('display label'),
 *   @String('display label'),
 *   @Date('display label', 'yyyy-MM-dd'),
 *   @Boolean('display label'),
 *   @Integer('display label')
 *
 *   @DisplayOrder(1) // display order for both crud table and crud form
 *
 *   ========================================
 *   Crud table specific
 *   ========================================
 *   @Identifier // column is the identifier for the table, include the identifier:true prop, required for table
 *   @TableDispalyed //Column is included in the list of columns that can be displayed in the table, but not specifically
 *                     initially displayed. If this is not present, column is hidden on the table
 *
 *   @TableDispalyedDefault //Column is included in the initially displayed columns
 *                            (replaces @TableDispalyed, don't need to use both)
 *
 * Example implementation:
 * export class Module {
 *   @Number('ID')
 *   @ReadOnly
 *   @DisplayOrder(1)
 *   @Identifier
 *   @TableDispalyedDefault
 *   id?: number;
 *
 *   @String('Code')
 *   @Required
 *   @DisplayOrder(3)
 *   @TableDispalyedDefault
 *   code?: string;
 *
 *   @Required
 *   @String('Display name')
 *   @DisplayOrder(4)
 *   @TableDispalyedDefault
 *   name?: string;
 *
 *   @Required
 *   @String('Description')
 *   @DisplayOrder(5)
 *   @TableDispalyedDefault
 *   description?: string;
 *
 *   @Required
 *   @String('System Code')
 *   @DisplayOrder(2)
 *   @Options([{ CONSOLE: 'Landlord portal' }, { PORTAL: 'Tenant portal' }])
 *   @TableDispalyed
 *   platformSystemCode?: string;
 *
 *   @Hidden
 *   serialVersionUID?: number;
 *
 *   @Boolean('Active')
 *   @DisplayOrder(6)
 *   active?: boolean = true;
 *
 *   @Date('label', 'yyyy-MM-dd')
 *   @Hidden
 *   createDate?: string;
 *
 *   @Hidden
 *   @Number('')
 *   createUserId?: number;
 *
 *   @Hidden
 *   @Date('')
 *   updateDate?: string;
 *
 *   @Hidden
 *   @Number('')
 *   updateUserId?: number;
 *
 *   constructor(obj: any = {}) {
 *     Object.assign(this, obj);
 *   }
 * }
 *
 * Will generate the following object that represents displayColumns:
 * displayColumns: AppTableDisplayColumn[] = [
 *     AppTableDisplayColumn.INSTANCE({colName: 'id', displayLabel: 'ID', identifier: true}),
 *     AppTableDisplayColumn.INSTANCE({ colName: 'code', displayLabel: 'Code' }),
 *     AppTableDisplayColumn.INSTANCE({ colName: 'name', displayLabel: 'Name' }),
 *     AppTableDisplayColumn.INSTANCE({colName: 'description', displayLabel: 'Description'}),
 *     AppTableDisplayColumn.INSTANCE({colName: 'platformSystemCode', displayLabel: 'System code', visible: false}),
 *   ];
 *
 *   @param classType - the class type
 */
export function generateTableColumns(classType: any): AppTableDisplayColumn[] {
  const instance = new classType();
  const displayColumns: any[] = [];
  Object.getOwnPropertyNames(instance).forEach((key) => {
    // Exclude constructor
    if (key !== 'constructor') {
      const description = Reflect.getMetadata('jsonforms:description', classType.prototype, key) || key;
      const order = Reflect.getMetadata('jsonforms:order', classType.prototype, key) || 1000;
      const isIdentifier = Reflect.getMetadata('table:identifier', classType.prototype, key) || false; // Ensure default false
      const isDisplayed = Reflect.getMetadata('table:displayed', classType.prototype, key) || false; // Ensure default false
      const isDisplayedDefault = Reflect.getMetadata('table:displayedDefault', classType.prototype, key) || isIdentifier || false; // Ensure default false
      const displayFunction = Reflect.getMetadata('table:displayFunction', classType.prototype, key) || null;
      const exportFunction = Reflect.getMetadata('table:exportFunction', classType.prototype, key) || null;
      const exportLabel = Reflect.getMetadata('table:exportLabel', classType.prototype, key) || null;
      const clickFunction = Reflect.getMetadata('table:clickFunction', classType.prototype, key) || null;
      const clickTooltip = Reflect.getMetadata('table:clickTooltip', classType.prototype, key) || null;

      if (isIdentifier || isDisplayed || isDisplayedDefault) {
        displayColumns.push(
          AppTableDisplayColumn.INSTANCE({
            colName: key,
            displayLabel: description,
            exportLabel: exportLabel,
            identifier: isIdentifier,
            visible: isDisplayedDefault,
            order: order,
            displayFunction: displayFunction,
            exportFunction: exportFunction,
            clickFunction: clickFunction,
            clickTooltip: clickTooltip,
          }));
      }
    }
  });

  displayColumns.sort((a, b) => a.order - b.order);

  return displayColumns;
}


/**
 * Generate a list of default search terms for a crud-table configuration for dynamically generating table data based on the
 * DefaultSearchTerms decorator.
 * e.g. :
 *
 * @DefaultSearchTerms(['active:true'])
 * export class TestClass {
 *    private field: string;
 * }
 *
 * will generate the following object that represents the default search terms:
 * ['active:true']
 * @param classType - the class type
 */
export function generateDefaultSearchTerms(classType: any): string[] {
  const instance = new classType();
  return Reflect.getMetadata('table:defaultSearchTerms', instance.constructor) ?? [];
}

/**
 * Identifies the default sort configuration for a table by examining class metadata.
 * Returns a MatSort object if exactly one property has the 'table:sortDefault' metadata,
 * otherwise returns null.
 *
 * The 'table:sortDefault' metadata should have a value of either 'asc' or 'desc'.
 *
 * Example usage:
 * export class MyClass {
 *   @SortDefault('asc')
 *   @String('Name')
 *   name?: string;
 *
 *   @String('Description')
 *   description?: string;
 * }
 *
 * @param classType - The class type to examine
 * @returns MatSort object with active and direction properties, or null
 */
export function identifyDefaultSort(classType: any): MatSort | null {
  const instance = new classType();
  let defaultSortColumn: string | null = null;
  let sortDirection: 'asc' | 'desc' = 'asc';
  let foundCount = 0;

  Object.getOwnPropertyNames(instance).forEach((key) => {
    if (key !== 'constructor') {
      const sortDefault = Reflect.getMetadata('table:sortDefault', classType.prototype, key);

      if (sortDefault) {
        foundCount++;
        defaultSortColumn = key;
        sortDirection = sortDefault === 'desc' ? 'desc' : 'asc';
      }
    }
  });

  // Return null if no default sort or multiple default sorts found
  if (foundCount !== 1 || !defaultSortColumn) {
    return null;
  }

  // Create and return MatSort object with the found configuration
  const matSort = new MatSort();
  matSort.active = defaultSortColumn;
  matSort.direction = sortDirection;

  return matSort;
}

/**
 * Decorator to specify the default sort configuration for a table column.
 * Only one column in a class should have this decorator.
 * @param direction - Sort direction, either 'asc' or 'desc'. Defaults to 'asc'
 */
export function SortDefault(direction: 'asc' | 'desc' = 'asc') {
  return function (target: any, propertyKey: string) {
    Reflect.defineMetadata('table:sortDefault', direction, target, propertyKey);
  };
}
